import { Form } from 'react-bootstrap';
import { Controller, type Control, type UseFormRegister, type UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextContentSep from '../common/TextContentSep';
import type { SampleFormData } from '../../utils/sampleFormSchema';
import SampleFormPathogenFields from './SampleFormPathogenFields';

export type SampleFormOptionalSectionsProps = {
  control: Control<SampleFormData>;
  register: UseFormRegister<SampleFormData>;
  setValue: UseFormSetValue<SampleFormData>;
  notesFoldOpen: boolean;
  onNotesFoldOpenChange: (open: boolean) => void;
  pathogenFoldOpen: boolean;
  onPathogenFoldOpenChange: (open: boolean) => void;
  pathogenOption: 'included' | 'excluded';
  onPathogenOptionChange: (value: 'included' | 'excluded') => void;
  /** 折りたたみ閉じ時に要約行へ出す（直前入力・編集値の可視化） */
  pathogenPreview?: string;
};

/** 備考・病原体の任意折りたたみ（新規は閉じ、編集で値があるときだけ開く） */
export default function SampleFormOptionalSections({
  control,
  register,
  setValue,
  notesFoldOpen,
  onNotesFoldOpenChange,
  pathogenFoldOpen,
  onPathogenFoldOpenChange,
  pathogenOption,
  onPathogenOptionChange,
  pathogenPreview = '',
}: SampleFormOptionalSectionsProps) {
  const { t } = useTranslation();
  const preview = pathogenPreview.trim();

  return (
    <>
      <details
        className="sample-form-fold sample-form-section"
        open={notesFoldOpen}
        onToggle={(e) => onNotesFoldOpenChange(e.currentTarget.open)}
      >
        <summary className="sample-form-fold-summary sample-accent-strip">
          <i className="fas fa-sticky-note sample-accent-strip-icon" aria-hidden="true" />
          {t('sample.notes')}
          <span className="text-muted fw-normal small">
            {' · '}
            {t('common.optional')}
          </span>
        </summary>
        <TextContentSep />
        <Form.Group className="mb-0">
          <Form.Label htmlFor="sample-notes" className="visually-hidden">
            {t('sample.notes')}
          </Form.Label>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <Form.Control
                as="textarea"
                id="sample-notes"
                name="notes"
                rows={3}
                {...field}
                value={field.value ?? ''}
                placeholder={t('sample.notes_placeholder')}
                autoComplete="off"
              />
            )}
          />
        </Form.Group>
      </details>

      <details
        className="sample-form-fold sample-form-section"
        open={pathogenFoldOpen}
        onToggle={(e) => onPathogenFoldOpenChange(e.currentTarget.open)}
      >
        <summary className="sample-form-fold-summary sample-accent-strip">
          <i className="fas fa-virus sample-accent-strip-icon" aria-hidden="true" />
          {t('sample.pathogen_information')}
          <span className="text-muted fw-normal small">
            {' · '}
            {t('common.optional')}
          </span>
          {preview ? (
            <span className="sample-form-fold-preview text-muted fw-normal small">
              {' · '}
              {preview}
            </span>
          ) : null}
        </summary>
        <TextContentSep />
        <div>
          <SampleFormPathogenFields
            embedded
            pathogenOption={pathogenOption}
            onPathogenOptionChange={onPathogenOptionChange}
            register={register}
            control={control}
            setValue={setValue}
          />
        </div>
      </details>
    </>
  );
}
